import { configureStore } from "@reduxjs/toolkit";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import rootReducer from "@redux/reducers/reducers";
import devToolsEnhancer from "redux-devtools-expo-dev-plugin";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { Platform } from "react-native";

// Create a safe web storage that handles missing localStorage gracefully
const createWebStorage = () => {
  if (Platform.OS !== "web") return AsyncStorage;

  try {
    // Check if localStorage is available
    if (typeof window !== "undefined" && window.localStorage) {
      // Use dynamic import to avoid the warning during SSR/initial load
      return require("redux-persist/lib/storage").default;
    }
  } catch (e) {
    // localStorage not available
  }

  // Fallback to a no-op storage (same as what redux-persist does, but without the warning)
  return {
    getItem: () => Promise.resolve(null),
    setItem: () => Promise.resolve(),
    removeItem: () => Promise.resolve(),
  };
};

const persistConfig = {
  key: "root",
  storage: createWebStorage(),
  stateReconciler: autoMergeLevel2,
  blacklist: ["appState", "translation", "searchFilters", "navigationHistory"],
};

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(
  persistConfig,
  rootReducer
);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: { warnAfter: 128 },
      serializableCheck: {
        warnAfter: 128,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools: false,
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(devToolsEnhancer()),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
